/**
 * Is a Fio API token about to stop working, or has it stopped?
 *
 * Fio's API documentation (API_Bankovnictvi.pdf v1.9, 16. 10. 2025):
 * - §2 "Vlastnosti tokenu": a token is valid for at most 180 days, chosen when
 *   it is created. With "automatické prodlužování" switched on, every login
 *   to Internetbanking or Smartbanking extends it to 180 days from that day.
 *   Tokens are managed in Internetbanking under Nastavení → API.
 * - §8 "Známé chybové stavy": HTTP 500 means "neexistující nebo neaktivní
 *   token". `fetch-fio-transactions` then stores `sync_error = "token_inactive"`
 *   and when it was first seen (`sync_error_since`). Fio's own outages answer
 *   500 too, with the same body, so the refusal is only "confirmed" after
 *   TOKEN_INACTIVE_CONFIRM_HOURS with no successful sync in between.
 *
 * `token_saved_at` is when the token was last saved in Zettl. It is not known
 * for tokens saved before the column existed (null), and an auto-renewed token
 * may be fine well past 180 days, so age only warns; it never blocks a sync.
 */

/** Warn from this age on: two weeks before the 180-day maximum. */
export const TOKEN_WARN_DAYS = 165;
export const TOKEN_MAX_DAYS = 180;

/** A refusal lasting this long without a success is a dead token, not an outage. */
export const TOKEN_INACTIVE_CONFIRM_HOURS = 20;

/** Fio Internetbanking sign-in. */
export const FIO_IB_URL = "https://ib.fio.cz/ib/login";

export type TokenHealth =
  /** Fio has refused the token (HTTP 500) for 20+ hours: it expired or was deactivated. */
  | "inactive"
  /** Fio refused the token recently: an expired token or a Fio outage, not yet known. */
  | "inactive_suspected"
  /** Zettl can no longer decrypt the stored token; it must be re-entered. */
  | "refresh_required"
  /** Saved 165+ days ago; it stops at 180 days unless auto-renewal is on. */
  | "expiring"
  | null;

export interface TokenHealthAccount {
  sync_error?: string | null;
  /** Absent until migration 20261103170000 is applied. */
  sync_error_since?: string | null;
  token_saved_at?: string | null;
}

/** A bank account as the token warnings list it. */
export interface FioTokenAccount extends TokenHealthAccount {
  id: string;
  name: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since the token was saved, or null when unknown. */
export function tokenAgeDays(savedAt: string | null | undefined, now: Date): number | null {
  if (!savedAt) return null;
  const saved = Date.parse(savedAt);
  if (Number.isNaN(saved)) return null;
  return Math.max(0, Math.floor((now.getTime() - saved) / DAY_MS));
}

export function tokenHealth(account: TokenHealthAccount, now: Date = new Date()): TokenHealth {
  if (account.sync_error === "token_inactive") {
    const since = account.sync_error_since ? Date.parse(account.sync_error_since) : NaN;
    const confirmed =
      !Number.isNaN(since) && now.getTime() - since >= TOKEN_INACTIVE_CONFIRM_HOURS * 3_600_000;
    return confirmed ? "inactive" : "inactive_suspected";
  }
  if (account.sync_error === "token_refresh_required") return "refresh_required";
  const age = tokenAgeDays(account.token_saved_at, now);
  return age !== null && age >= TOKEN_WARN_DAYS ? "expiring" : null;
}

/** Days left of the 180, never below zero; null when the age is unknown. */
export function tokenDaysLeft(savedAt: string | null | undefined, now: Date): number | null {
  const age = tokenAgeDays(savedAt, now);
  return age === null ? null : Math.max(0, TOKEN_MAX_DAYS - age);
}
